import { useEffect, useMemo, useState } from 'react';

import { styled } from '@mui/material';

import { useNavigate, useParams } from 'react-router';

import { isBefore } from 'date-fns';

import { Box, Skeleton } from '@app-velocity/ui';
import { PersonaSelector } from '@app-velocity/modules/personas';

import {
  AIChat,
  AiChatContext,
  AIReviewStatus,
  ScenarioRoutePaths,
  useGetScenarioShortSummaryQuery,
  useSimulationContext,
} from '@app-velocity/modules/scenarios';

import { useGuestAccess } from '@app-velocity/config';

import { useCurrentUser } from '@app-velocity/libs/auth';

import { SimulationStepsLimitNotice } from '@app-velocity/libs/shared';

import { SimulationChallenge } from './components/SimulationChallenge';
import { selectNode } from '../../../../../../../core';
import { PageStatusHeader } from '../../../../../../components/PageStatusHeader';
import { GuestNotice } from './components/GuestNotice/GuestNotice';
import { InteractiveProbabilityLegend } from './components/InteractivityProbabilityLegend';
import { NotificationBox } from './components/NotificationBox';

const ReviewContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledContent = styled('div')(({ theme }) => ({
  background: '#F0F2F4',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(4),
  gap: theme.spacing(3),
  flex: '1',
  width: '100%',
  overflow: 'auto',
}));

const StyledPersonaSelector = styled(PersonaSelector)({
  width: 200,
  '.MuiSelect-select': {
    backgroundColor: '#fff',
  },
});

export const SimulationContent = () => {
  const { scenario, review, mode } = useSimulationContext();
  const currentUser = useCurrentUser();

  const { persistGuestState } = useGuestAccess();
  const [persona, setPersona] = useState<string>();
  const navigate = useNavigate();
  const { teamId, workspaceId, scenarioId, reviewId } = useParams<{
    teamId: string;
    workspaceId: string;
    scenarioId: string;
    reviewId: string;
  }>();

  const {
    data: summary,
    isLoading: isSummaryLoading,
    refetch: refetchShortSummary,
  } = useGetScenarioShortSummaryQuery(workspaceId, scenarioId);

  const reviewStatus = useMemo(() => {
    if (!review) return null;

    switch (review.reviewStatus) {
      case 'CANCELED': {
        if (mode === 'guest') return 'paused';
        if (
          review.finishStatus &&
          [
            'ABORTED',
            'ABORTED_CYCLE_DETECTED',
            'ABORTED_PAGE_CYCLE_DETECTED',
          ].includes(review.finishStatus)
        )
          return 'warning';

        return 'error';
      }

      case 'COMPLETED':
        if (mode === 'guest') return 'paused';

        return 'success';
      case 'IN_PROGRESS':
        return 'pending';
    }

    return null;
  }, [review]);

  useEffect(() => {
    if (!reviewStatus) return;

    if (mode !== 'guest') return;

    if (reviewStatus === 'paused') {
      persistGuestState({
        type: 'simulation',
        workspaceId: workspaceId!,
        challengeId: scenarioId!,
        reviewId: reviewId!,
      });
    }
  }, [reviewStatus]);

  useEffect(() => {
    if (!review?.persona) return;

    setPersona(review.persona || 'default');

    if (review.reviewStatus !== 'IN_PROGRESS') {
      refetchShortSummary();
    }
  }, [review]);

  const latestSimulationWithSelectedPersona = useMemo(() => {
    if (!review || !persona) return null;
    const sortedByCreated = (summary?.simulations || [])
      .filter((sim) => !!sim.created)
      .sort((a, b) =>
        isBefore(new Date(a.created!), new Date(b.created!)) ? 1 : -1
      );

    return sortedByCreated.find((sim) => sim.persona === persona) || null;
  }, [persona, summary, review]);

  useEffect(() => {
    if (!workspaceId || !teamId || !scenarioId) return;

    if (
      review?.reviewStatus !== 'IN_PROGRESS' &&
      latestSimulationWithSelectedPersona?.reviewId &&
      reviewId !== latestSimulationWithSelectedPersona?.reviewId
    ) {
      navigate(
        ScenarioRoutePaths.challengeSimulation(
          teamId,
          workspaceId,
          scenarioId,
          latestSimulationWithSelectedPersona?.reviewId
        )
      );
    }
  }, [latestSimulationWithSelectedPersona, review]);

  const goBack = () => {
    if (!workspaceId || !teamId || !scenarioId) return;
    navigate(
      ScenarioRoutePaths.challengeDetails(teamId, workspaceId, scenarioId)
    );
  };

  const navigateToElement = (elementNodeId: string) => {
    selectNode(elementNodeId);
  };

  const isLoading = !reviewId || !review || !persona;

  const shouldDisplayStatus =
    (latestSimulationWithSelectedPersona && mode === 'authorized') ||
    review?.reviewStatus === 'IN_PROGRESS';

  const shouldDisplayNotificationBox =
    mode === 'authorized' &&
    review?.reviewStatus === 'IN_PROGRESS' &&
    !currentUser?.marketingAccepted;

  const changePersona = (newPersona: string) => {
    setPersona(newPersona);
  };

  if (isLoading || isSummaryLoading)
    return (
      <StyledContent>
        <Skeleton height={58} borderRadius={0} margin="0 0 4px 0" />
        <Box sx={{ padding: 4 }}>
          <Skeleton height={159} margin="0" />
        </Box>
      </StyledContent>
    );

  return (
    <>
      <PageStatusHeader
        status={reviewStatus}
        content={
          <StyledPersonaSelector
            teamId={teamId!}
            onChange={changePersona}
            disabled={reviewStatus === 'pending' || isSummaryLoading}
            value={persona || review.persona || 'default'}
          />
        }
        onBack={goBack}
      />
      <StyledContent>
        <ReviewContainer>
          <AiChatContext.Provider
            value={{
              scenario: scenario!,
              review: review!,
              navigateToElement,
              latestSimulationWithSelectedPersona:
                latestSimulationWithSelectedPersona,
              persona,
            }}
          >
            <SimulationChallenge />
            {latestSimulationWithSelectedPersona ? (
              <InteractiveProbabilityLegend />
            ) : null}
            <SimulationStepsLimitNotice
              teamId={teamId!}
              workspaceId={workspaceId!}
            />
            <AIChat />
            {shouldDisplayNotificationBox ? <NotificationBox /> : null}
            {shouldDisplayStatus ? (
              <AIReviewStatus
                review={review!}
                action={
                  review.reviewStatus === 'IN_PROGRESS' ? (
                    <AIReviewStatus.Stop />
                  ) : null
                }
              >
                <AIReviewStatus.Actions />
              </AIReviewStatus>
            ) : null}
            <GuestNotice />
          </AiChatContext.Provider>
        </ReviewContainer>
      </StyledContent>
    </>
  );
};
